import React, { useCallback, useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import { debounce } from "lodash";
import dayjs from "dayjs";
import useApi from "../hook/useApi";
import Loader from "./Loader/Loader";
import Paggination from "./Paggination";
import CouponModal from "./model/CouponModal";
import { CURRENCY_CODE } from "../utils/constant";

const isCouponActive = (value) => {
  if (value === false || value === "false" || value === 0 || value === "0") {
    return false;
  }
  if (value === true || value === "true" || value === 1 || value === "1") {
    return true;
  }
  return Boolean(value);
};

const CouponLayer = () => {
  const [response, setResponse] = useState(null); // full API payload
  const [listLoading, setListLoading] = useState(false);
  const [rowBusyId, setRowBusyId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [modalData, setModalData] = useState(null);

  const [query, setQuery] = useState({
    pageNo: 1,
    limitVal: 50,
    search: "",
    couponType: "", // Add couponType to query state (empty string for "All")
  });

  const { AsyncGetApiCall, AsyncPatchAPICall } = useApi();

  const handleModalClose = (shouldRefresh = false) => {
    setShowModal(false);
    setModalData(null);
    if (shouldRefresh) fetchCoupons();
  };

  const restoreCoupon = async (id) => {
    setRowBusyId(id);
    const result = await AsyncPatchAPICall(`/coupon/update/${id}`, {
      isDeleted: false,
    });

    if (result?.errors) {
      toast.error(result.errors[0]);
    } else if (result?.success) {
      toast.success("Coupon restored successfully");
      await fetchCoupons();
    } else {
      toast.error("Failed to restore coupon");
    }

    setRowBusyId(null);
  };

  const fetchCoupons = async () => {
    setListLoading(true);
    const queryParams = {
      pageNo: query.pageNo,
      limitVal: query.limitVal,
      search: query.search,
      ...(query.couponType && { couponType: query.couponType }),
    };
    const result = await AsyncGetApiCall("/coupon/list", queryParams);

    if (result?.errors) {
      toast.error(result.errors[0]);
    } else {
      setResponse(result);
    }
    setListLoading(false);
  };

  useEffect(() => {
    fetchCoupons();
  }, [query]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value) => {
      setQuery((prev) => ({ ...prev, search: value, pageNo: 1 }));
    }, 500),
    []
  );

  const handleSearchChange = (e) => debouncedSearch(e.target.value);

  const handleCouponTypeChange = (e) => {
    const couponType = e.target.value;
    setQuery((prev) => ({
      ...prev,
      couponType,
      pageNo: 1, // Reset to first page on coupon type change
    }));
  };

  const deleteCoupon = async (id) => {
    const previousResponse = response;
    setRowBusyId(id);
    setResponse((prev) => {
      if (!prev?.data) return prev;
      return {
        ...prev,
        data: prev.data.map((coupon) =>
          coupon._id === id ? { ...coupon, isDeleted: true } : coupon
        ),
      };
    });

    const result = await AsyncPatchAPICall(`/coupon/update/${id}`, {
      isDeleted: true,
    });

    if (result?.errors) {
      toast.error(result.errors[0]);
      setResponse(previousResponse);
    } else if (result?.success) {
      toast.success(result.message || "Coupon deleted successfully");
      await fetchCoupons();
    } else {
      setResponse(previousResponse);
      toast.error("Failed to delete coupon");
    }

    setRowBusyId(null);
  };

  const handleStatusChange = async (id, newIsActive) => {
    const previousResponse = response;
    setRowBusyId(id);
    setResponse((prev) => {
      if (!prev?.data) return prev;
      return {
        ...prev,
        data: prev.data.map((coupon) =>
          coupon._id === id ? { ...coupon, isActive: newIsActive } : coupon
        ),
      };
    });

    try {
      const result = await AsyncPatchAPICall(`/coupon/toggle-status`, {
        id,
        isActive: newIsActive,
      });
      if (result?.errors) {
        toast.error(result.errors[0]);
        setResponse(previousResponse);
      } else if (result?.success) {
        toast.success(result.message || "Coupon status updated successfully");
        if (result?.data) {
          setResponse((prev) => {
            if (!prev?.data) return prev;
            return {
              ...prev,
              data: prev.data.map((coupon) =>
                coupon._id === id ? { ...coupon, ...result.data } : coupon
              ),
            };
          });
        } else {
          await fetchCoupons();
        }
      } else {
        setResponse(previousResponse);
        toast.error("Failed to update coupon status");
      }
    } catch (error) {
      console.error("Something went wrong", error);
      setResponse(previousResponse);
      toast.error("Something went wrong");
    } finally {
      setRowBusyId(null);
    }
  };

  const coupons = response?.data ?? [];
  const pagination = response?.pagination ?? {};
  const { total = 0, totalPages = 0, currentPage = 1 } = pagination;

  return (
    <>
      {listLoading && <Loader />}
      <div className="card h-100 p-0 radius-12">
        {/* ─────────────────── Header ─────────────────── */}
        <div className="card-header border-bottom bg-base py-16 px-24 d-flex flex-wrap gap-3 justify-content-between">
          <div className="d-flex align-items-center gap-3 flex-wrap w-100">
            {/* Limit selector */}
            <span className="text-md fw-medium text-secondary-light">Show</span>
            <select
              className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
              value={query.limitVal}
              onChange={(e) =>
                setQuery((prev) => ({
                  ...prev,
                  limitVal: Number(e.target.value),
                  pageNo: 1,
                }))
              }
            >
              {Array.from({ length: 20 }, (_, i) => (i + 1) * 5).map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>

            {/* Coupon Type selector */}
            <span className="text-md fw-medium text-secondary-light">Coupon Type</span>
            <select
              className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
              value={query.couponType}
              onChange={handleCouponTypeChange}
              aria-label="Filter by coupon type"
            >
              <option value="">All</option>
              <option value="HOTEL">Hotel</option>
              <option value="FLIGHT">Flight</option>
              <option value="ESIM">ESim</option>
            </select>

            {/* Search */}
            <form className="navbar-search">
              <input
                type="text"
                className="bg-base h-40-px w-auto"
                placeholder="Search by coupon code"
                onChange={handleSearchChange}
              />
              <Icon icon="ion:search-outline" className="icon" />
            </form>

            {/* Add button */}
            <button
              type="button"
              className="btn btn-primary-600 radius-8 px-17 py-9 d-flex align-items-center gap-2 ms-auto"
              onClick={() => {
                setModalMode("create");
                setModalData(null);
                setShowModal(true);
              }}
              disabled={listLoading}
            >
              <Icon icon="mdi:gift-outline" style={{ fontSize: 20 }} />
              Create Coupon
            </button>
          </div>
        </div>

        {/* ─────────────────── Table ─────────────────── */}
        <div className="card-body p-24">
          <div className="table-responsive scroll-sm">
            <table className="table bordered-table sm-table mb-0">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Amount</th>
                  <th>Coupon Code</th>
                  <th>Coupon Type</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th style={{ textAlign: "center" }}>Action</th>
                </tr>
              </thead>

              <tbody>
                {coupons.length ? (
                  coupons.map((c, i) => {
                    const rowNo = (currentPage - 1) * query.limitVal + i + 1;
                    const active = isCouponActive(c?.isActive);
                    const rowBusy = rowBusyId === c._id;
                    const description = c?.description || "";

                    return (
                      <tr key={c._id}>
                        <td>{rowNo}</td>

                        {/* Amount with currency icon */}
                        <td className="flex items-center justify-center gap-2">
                          {CURRENCY_CODE.EUR}{" "}{c.amount}
                        </td>

                        <td>{c.couponCode}</td>
                        <td>{c.couponType}</td>
                        <td>{dayjs(c.startDate).format("DD-MMM-YYYY")}</td>
                        <td>{dayjs(c.endDate).format("DD-MMM-YYYY")}</td>
                        <td>
                          {description.length > 15
                            ? description.slice(0, 15) + "..."
                            : description || "—"}
                        </td>

                        {/* Status toggle */}
                        <td>
                          <div
                            className={`form-switch switch-success d-flex align-items-center gap-3 ${rowBusy ? "opacity-50" : ""}`}
                          >
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              id={`status-${c._id}`}
                              checked={active}
                              onChange={(e) =>
                                handleStatusChange(c._id, e.target.checked)
                              }
                              disabled={rowBusy || c?.isDeleted}
                            />
                            <label
                              className={`form-check-label line-height-1 fw-medium text-sm mb-0 ${active ? "text-success-600" : "text-danger-600"}`}
                              htmlFor={`status-${c._id}`}
                            >
                              {active ? "Active" : "Inactive"}
                            </label>
                          </div>
                        </td>

                        {/* Actions */}
                        <td className="text-center">
                          <div className="d-flex gap-10 justify-content-center">
                            {c.isDeleted ? (
                              <button
                                className="bg-info-focus bg-hover-info-200 text-info-600 w-40-px h-40-px rounded-circle d-flex justify-content-center align-items-center"
                                title="Retrieve Coupon"
                                onClick={() => restoreCoupon(c._id)}
                                disabled={rowBusy}
                              >
                                <Icon icon="mdi:backup-restore" />
                              </button>
                            ) : (
                              <>
                                <button
                                  className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                  title="Edit Coupon"
                                  onClick={() => {
                                    setModalMode("update");
                                    setModalData(c);
                                    setShowModal(true);
                                  }}
                                  disabled={rowBusy || listLoading}
                                >
                                  <Icon icon="lucide:edit" />
                                  <span className="tooltip-text">Edit</span>
                                </button>

                                <button
                                  className="bg-danger-focus bg-hover-danger-200 text-danger-600 w-40-px h-40-px rounded-circle d-flex justify-content-center align-items-center"
                                  title="Delete Coupon"
                                  onClick={() => {
                                    if (
                                      window.confirm(
                                        "Do you want to delete this coupon?"
                                      )
                                    ) {
                                      deleteCoupon(c._id)
                                    }
                                  }}
                                  disabled={rowBusy || listLoading}
                                >
                                  <Icon icon="fluent:delete-24-regular" />
                                  <span className="tooltip-text">Delete</span>
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center">
                      No coupons found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ─────────────────── Pagination ─────────────────── */}
          <Paggination
            total={total}
            totalPages={totalPages}
            currentPage={currentPage}
            startFrom={(currentPage - 1) * query.limitVal + 1}
            endTo={(currentPage - 1) * query.limitVal + coupons.length}
            setPageNo={(page) =>
              setQuery((prev) => ({ ...prev, pageNo: page }))
            }
          />
        </div>
      </div>

      <CouponModal
        show={showModal}
        handleClose={handleModalClose}
        mode={modalMode}
        data={modalData}
      />
    </>
  );
};

export default CouponLayer;